import { useDispatch, useSelector } from 'react-redux'

import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderActions,
  CardHeaderTitle,
  DeleteButton,
} from 'controlled-components'
import { buildTranslate } from 'locales'
import {
  PickerSliceState,
  removePicker,
  selectBottomOperator,
  selectLevel1Label,
  selectLevel2OperatorOptions,
} from 'slices/aiSegmentBuilder/picker'

import OperatorContainer from '../Operator.container'
import CanonicalLevelContainer from './CanonicalLevel.container'
import LevelModePillContainer from './LevelModePill.container'

const t = buildTranslate('components.ai.picker.canonical')

type RootState = { picker: PickerSliceState }

type Props = {
  pickerId: number
  index: number
  canRemove: boolean
  disabled: boolean
}

/** One card of a canonical picker: a level 1 pick and the level 2 picks under it */
const CanonicalPickerCardContainer = ({
  pickerId,
  index,
  canRemove,
  disabled,
}: Props) => {
  const dispatch = useDispatch()
  const label = useSelector((state: RootState) =>
    selectLevel1Label(state, pickerId)
  )
  const operator = useSelector((state: RootState) =>
    selectBottomOperator(state, pickerId)
  )
  const operatorOptions = useSelector((state: RootState) =>
    selectLevel2OperatorOptions(state, pickerId)
  )

  const title = t('card_title', {
    label: label.toUpperCase(),
    number: index + 1,
  })
  const showLevel2 =
    operatorOptions.find(option => option.id === operator)?.requiresValues !==
    false
  const showRemove = canRemove && !disabled

  return (
    <Card variant="outlined" className="mb-s">
      <CardHeader>
        <div className="d-flex align-items-center">
          <CardHeaderTitle className="mr-xs">{title}</CardHeaderTitle>
          <LevelModePillContainer pickerId={pickerId} level="level1" />
        </div>
        {showRemove && (
          <CardHeaderActions>
            <DeleteButton
              aria-label={t('remove_card', { title })}
              onClick={() => dispatch(removePicker({ pickerId }))}
            />
          </CardHeaderActions>
        )}
      </CardHeader>
      <CardBody padding="md">
        <CanonicalLevelContainer
          pickerId={pickerId}
          level="level1"
          disabled={disabled}
        />
        <div className="mt-xs">
          <div className="d-flex align-items-center flex-wrap">
            <OperatorContainer pickerId={pickerId} disabled={disabled} />
            {showLevel2 && (
              <span className="ml-xs">
                <LevelModePillContainer pickerId={pickerId} level="level2" />
              </span>
            )}
          </div>
          {showLevel2 && (
            <div className="mt-xs">
              <CanonicalLevelContainer
                pickerId={pickerId}
                level="level2"
                disabled={disabled}
              />
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  )
}

export default CanonicalPickerCardContainer
